import React, { useState, useEffect, useCallback } from 'react';
import {
  Bot,
  Plus,
  Search,
  Settings,
  Trash2,
  Eye,
  EyeOff,
  TestTube,
  Star,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit,
  Save,
  X
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface AIProvider {
  id: number;
  name: string;
  provider_type: string;
  api_key_masked: string;
  api_url: string;
  model_name: string;
  max_tokens: number;
  temperature: number;
  is_active: boolean;
  is_default: boolean;
  configuration: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface ProviderType {
  value: string;
  label: string;
  description: string;
  defaultUrl: string;
  models: string[];
}

interface AIProviderFormData {
  name: string;
  provider_type: string;
  api_key: string;
  api_url: string;
  model_name: string;
  max_tokens: number;
  temperature: number;
  is_active: boolean;
  is_default: boolean;
}

const AIProviders: React.FC = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [showApiKey, setShowApiKey] = useState<Record<number, boolean>>({});
  const [testingProvider, setTestingProvider] = useState<number | null>(null);

  // Form state
  const [formData, setFormData] = useState<AIProviderFormData>({
    name: '',
    provider_type: '',
    api_key: '',
    api_url: '',
    model_name: '',
    max_tokens: 4000,
    temperature: 0.7,
    is_active: true,
    is_default: false
  });

  // Fetch providers
  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(typeFilter && { provider_type: typeFilter })
      });

      const response = await api.get(`/api/admin/ai-providers?${params}`);
      setProviders(response.data.providers || []);
    } catch (error: any) {
      console.error('Error fetching AI providers:', error);
      toast.error('Failed to fetch AI providers');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, typeFilter]);

  // Fetch provider types
  const fetchProviderTypes = async () => {
    try {
      const response = await api.get('/api/admin/ai-providers/meta/provider-types');
      setProviderTypes(response.data.providerTypes || []);
    } catch (error: any) {
      console.error('Error fetching provider types:', error);
      toast.error('Failed to fetch provider types');
    }
  };

  useEffect(() => {
    fetchProviders();
    fetchProviderTypes();
  }, [fetchProviders]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.provider_type || !formData.api_key) {
      toast.error('Name, provider type, and API key are required');
      return;
    }

    try {
      setActionLoading(editingProvider?.id || -1);
      
      if (editingProvider) {
        await api.put(`/api/admin/ai-providers/${editingProvider.id}`, formData);
        toast.success('AI provider updated successfully');
      } else {
        await api.post('/api/admin/ai-providers', formData);
        toast.success('AI provider created successfully');
      }

      setShowModal(false);
      setEditingProvider(null);
      resetForm();
      fetchProviders();
    } catch (error: any) {
      console.error('Error saving AI provider:', error);
      toast.error(error.response?.data?.error || 'Failed to save AI provider');
    } finally {
      setActionLoading(null);
    }
  };

  // Handle provider actions
  const handleProviderAction = async (providerId: number, action: string) => {
    try {
      setActionLoading(providerId);

      switch (action) {
        case 'toggle':
          const provider = providers.find(p => p.id === providerId);
          await api.put(`/api/admin/ai-providers/${providerId}`, {
            is_active: !provider?.is_active
          });
          toast.success(`Provider ${provider?.is_active ? 'deactivated' : 'activated'} successfully`);
          break;

        case 'delete':
          if (window.confirm('Are you sure you want to delete this AI provider?')) {
            await api.delete(`/api/admin/ai-providers/${providerId}`);
            toast.success('AI provider deleted successfully');
          }
          break;

        case 'setDefault':
          await api.put(`/api/admin/ai-providers/${providerId}`, {
            is_default: true
          });
          toast.success('Set as default provider successfully');
          break;
      }

      fetchProviders();
    } catch (error: any) {
      console.error(`Error ${action} provider:`, error);
      toast.error(error.response?.data?.error || `Failed to ${action} provider`);
    } finally {
      setActionLoading(null);
    }
  };

  // Test provider connection
  const testProviderConnection = async (providerId: number) => {
    try {
      setTestingProvider(providerId);
      const response = await api.post(`/api/admin/ai-providers/${providerId}/test`);
      
      if (response.data.result.success) {
        toast.success('Connection test successful');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error: any) {
      console.error('Error testing provider:', error);
      toast.error(error.response?.data?.error || 'Connection test failed');
    } finally {
      setTestingProvider(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      provider_type: '',
      api_key: '',
      api_url: '',
      model_name: '',
      max_tokens: 4000,
      temperature: 0.7,
      is_active: true,
      is_default: false
    });
  };

  // Handle edit
  const handleEdit = (provider: AIProvider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      provider_type: provider.provider_type,
      api_key: '', // Don't populate API key for security
      api_url: provider.api_url || '',
      model_name: provider.model_name || '',
      max_tokens: provider.max_tokens,
      temperature: provider.temperature,
      is_active: provider.is_active,
      is_default: provider.is_default
    });
    setShowModal(true);
  };

  // Handle provider type change
  const handleProviderTypeChange = (value: string) => {
    const selectedType = providerTypes.find(type => type.value === value);
    setFormData(prev => ({
      ...prev,
      provider_type: value,
      api_url: selectedType?.defaultUrl || '',
      model_name: selectedType?.models[0] || ''
    }));
  };

  // Toggle API key visibility
  const toggleApiKeyVisibility = (providerId: number) => {
    setShowApiKey(prev => ({
      ...prev,
      [providerId]: !prev[providerId]
    }));
  };

  // Get provider type label
  const getProviderTypeLabel = (type: string) => {
    return providerTypes.find(pt => pt.value === type)?.label || type;
  };

  // Filtered providers
  const filteredProviders = providers.filter(provider => {
    const matchesSearch = !searchTerm || 
      provider.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      provider.provider_type.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !typeFilter || provider.provider_type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bot className="w-8 h-8 text-blue-600" />
            AI Providers Management
          </h2>
          <p className="text-gray-600">Configure AI providers and their API keys</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingProvider(null);
            setShowModal(true);
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Add Provider
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search providers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Types</option>
            {providerTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('');
            }}
            className="px-4 py-2 border rounded-md hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* Providers Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Provider
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading providers...
                  </td>
                </tr>
              ) : filteredProviders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No AI providers found
                  </td>
                </tr>
              ) : (
                filteredProviders.map((provider) => (
                  <tr key={provider.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                            {provider.name}
                            {provider.is_default && (
                              <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            Created {new Date(provider.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {getProviderTypeLabel(provider.provider_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {showApiKey[provider.id] ? '****' : provider.api_key_masked}
                        </code>
                        <button
                          onClick={() => toggleApiKeyVisibility(provider.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showApiKey[provider.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {provider.model_name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {provider.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => testProviderConnection(provider.id)}
                          disabled={testingProvider === provider.id}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                          title="Test Connection"
                        >
                          <TestTube className={`w-4 h-4 ${testingProvider === provider.id ? 'animate-spin' : ''}`} />
                        </button>
                        
                        <button
                          onClick={() => handleEdit(provider)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => handleProviderAction(provider.id, 'toggle')}
                          disabled={actionLoading === provider.id}
                          className={`${provider.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'} disabled:opacity-50`}
                          title={provider.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {provider.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                        </button>
                        
                        {!provider.is_default && (
                          <button
                            onClick={() => handleProviderAction(provider.id, 'setDefault')}
                            disabled={actionLoading === provider.id}
                            className="text-yellow-600 hover:text-yellow-900 disabled:opacity-50"
                            title="Set as Default"
                          >
                            <Star className="w-4 h-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleProviderAction(provider.id, 'delete')}
                          disabled={actionLoading === provider.id}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingProvider ? 'Edit AI Provider' : 'Add AI Provider'}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingProvider(null);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., OpenAI GPT-4"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provider Type *
                    </label>
                    <select
                      value={formData.provider_type}
                      onChange={(e) => handleProviderTypeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select Provider Type</option>
                      {providerTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API Key *
                  </label>
                  <input
                    type="password"
                    value={formData.api_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={editingProvider ? "Enter new API key (leave blank to keep current)" : "Enter API key"}
                    required={!editingProvider}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    API URL
                  </label>
                  <input
                    type="url"
                    value={formData.api_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, api_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://api.example.com"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Model Name
                    </label>
                    <input
                      type="text"
                      value={formData.model_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, model_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="gpt-4"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      value={formData.max_tokens}
                      onChange={(e) => setFormData(prev => ({ ...prev, max_tokens: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="1"
                      max="200000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Temperature
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.temperature}
                      onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="2"
                    />
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_default: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Set as Default</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingProvider(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading !== null}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading !== null ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {editingProvider ? 'Update' : 'Create'} Provider
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIProviders;